/** SSO Entra via Cognito: Authorization Code + PKCE (S256) dal browser. Il verifier, lo state e il nonce stanno in sessionStorage. */
import { api } from "../api/client";

export interface SsoConfig {
  enabled: boolean;
  authorizeUrl?: string;
  tokenUrl?: string;
  logoutUrl?: string;
  clientId?: string;
  redirectUri?: string;
  postLogoutUri?: string;
  scope?: string;
  identityProvider?: string;
}

const KEY = "ap_sso";
interface Pending {
  verifier: string;
  state: string;
  nonce: string;
  from?: string;
}

const b64url = (buf: ArrayBuffer | Uint8Array) => {
  const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  let s = "";
  bytes.forEach((b) => (s += String.fromCharCode(b)));
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};
const random = (n: number) => b64url(crypto.getRandomValues(new Uint8Array(n)));

export const fetchSsoConfig = () => api.get<SsoConfig>("/auth/sso/config");

/** Parte il flusso: salva verifier/state/nonce e porta il browser alla pagina di accesso di Cognito (che rimanda a Entra). */
export async function startSso(cfg: SsoConfig, from?: string) {
  const verifier = random(48);
  const challenge = b64url(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier)));
  const pending: Pending = { verifier, state: random(24), nonce: random(24), ...(from ? { from } : {}) };
  sessionStorage.setItem(KEY, JSON.stringify(pending));
  const q = new URLSearchParams({
    response_type: "code",
    client_id: cfg.clientId!,
    redirect_uri: cfg.redirectUri!,
    scope: cfg.scope ?? "openid email profile",
    state: pending.state,
    nonce: pending.nonce,
    code_challenge: challenge,
    code_challenge_method: "S256",
    ...(cfg.identityProvider ? { identity_provider: cfg.identityProvider } : {}),
  });
  window.location.assign(`${cfg.authorizeUrl}?${q.toString()}`);
}

export class SsoError extends Error {}

/** Chiude il flusso: controlla lo state, scambia il codice su Cognito e apre la sessione sul backend. Restituisce la pagina di destinazione. */
export async function finishSso(params: URLSearchParams): Promise<string> {
  const raw = sessionStorage.getItem(KEY);
  sessionStorage.removeItem(KEY); // il codice e' monouso: un secondo tentativo riparte da capo
  const err = params.get("error");
  if (err) throw new SsoError(params.get("error_description") || err);
  const code = params.get("code");
  const state = params.get("state");
  if (!raw || !code || !state) throw new SsoError("sessione di accesso non trovata");
  const pending = JSON.parse(raw) as Pending;
  if (pending.state !== state) throw new SsoError("state non corrispondente");
  const cfg = await fetchSsoConfig();
  if (!cfg.enabled) throw new SsoError("SSO non attivo");
  const res = await fetch(cfg.tokenUrl!, {
    method: "POST",
    headers: { "content-type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      grant_type: "authorization_code",
      client_id: cfg.clientId!,
      code,
      redirect_uri: cfg.redirectUri!,
      code_verifier: pending.verifier,
    }),
  });
  const tok = (await res.json().catch(() => ({}))) as { id_token?: string; error?: string };
  if (!res.ok || !tok.id_token) throw new SsoError(tok.error ?? `scambio del codice fallito (${res.status})`);
  await api.post("/auth/sso/session", { idToken: tok.id_token, nonce: pending.nonce });
  return pending.from && pending.from.startsWith("/") && pending.from !== "/login" ? pending.from : "/";
}
